/**
 * Interfaces para el flujo de selección de tiendas V3 (Flujo Universal)
 * Basadas en los endpoints:
 * - GET /v1/stores
 * - GET /v1/store/{id}/office/nearby
 * - GET /v1/request/{id}/pre-approval
 */

// ============ CATEGORÍAS ============

/**
 * Categoría embebida en cada tienda
 * Viene del endpoint GET /v1/stores
 */
export interface StoreCategoryV3 {
  id: string;
  code: string;
  name: string;
  type: string;
  image_url: string;
  icon_url: string;
}

// ============ TIENDAS ============

/**
 * Tienda del listado principal
 * Viene del endpoint GET /v1/stores
 */
export interface StoreV3 {
  id: string;
  name: string;
  address: string;
  phone: string;
  region: string;
  zone: string;
  distance: number;
  logo: string;
  min_installment?: number; // Abono mínimo (porcentaje)
  categories: StoreCategoryV3[];
  offices: OfficeV3[];
}

/**
 * Respuesta del endpoint GET /v1/stores
 */
export interface StoreListResponseV3 {
  list: StoreV3[];
  total_records: number;
}

// ============ SUCURSALES/OFICINAS ============

/**
 * Sucursal/Oficina de una tienda
 * Viene del endpoint GET /v1/store/{id}/office/nearby
 */
export interface OfficeV3 {
  id: string;
  name: string;
  address: string;
  phone: string;
  region: string;
  zone: string;
  distance: number;
  min_installment?: number; // Abono mínimo (porcentaje)
  logo?: string;
  categories?: StoreCategoryV3[];
  offices?: OfficeV3[] | null;
}

/**
 * Respuesta del endpoint GET /v1/store/{id}/office/nearby
 */
export interface OfficeListResponseV3 {
  list: OfficeV3[];
  total_records: number;
}

// ============ PRE-APROBACIÓN ============

/**
 * Datos de pre-aprobación del cliente
 * Viene del endpoint GET /v1/request/{id}/pre-approval
 */
export interface PreApprovalV3 {
  amount: number;
}

// ============ FILTROS ============

/**
 * Filtros para el endpoint GET /v1/stores
 * maxDistance y administrativeRegions son query params del backend
 * categoryCode y searchTerm son filtros locales (frontend)
 */
export interface StoreFilterV3 {
  maxDistance?: number;           // KM - Backend filter
  administrativeRegions?: string; // Región administrativa - Backend filter
  categoryCode?: string;          // Filtro local por categoría
  searchTerm?: string;            // Búsqueda local por nombre
}

// ============ GEOLOCALIZACIÓN ============

/**
 * Ubicación del usuario obtenida via HTML5 Geolocation API
 */
export interface UserLocationV3 {
  latitude: number;
  longitude: number;
  timestamp: number;
  granted: boolean;
}

// ============ OPCIONES DE UI ============

/**
 * Opción de distancia para el dropdown de filtros
 */
export interface DistanceOptionV3 {
  id: string;
  name: string;
  value: number; // KM
}

/**
 * Opción de región administrativa para el dropdown de filtros
 */
export interface RegionOptionV3 {
  id: string;
  name: string;
  value: string;
}

// ============ ESTADOS DEL COMPONENTE ============

/**
 * Estados posibles del componente de listado de tiendas
 */
export type ComponentStateV3 = 'loading' | 'loaded' | 'error' | 'empty';
